import {
  useQuery,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";

export const GLOBAL_FLATTEN_SAFE_STATE_QUERY_KEY = [
  "global-flatten-safe-state",
] as const satisfies QueryKey;

export const GLOBAL_FLATTEN_CONNECTION_EPOCH_QUERY_KEY = [
  "global-flatten-connection-epoch",
] as const satisfies QueryKey;

export const GLOBAL_FLATTEN_ATTEMPT_QUERY_KEY = [
  "global-flatten-attempt",
] as const satisfies QueryKey;

export type GlobalFlattenProofScope = "global" | "subset";
export type GlobalFlattenMode = "live" | "demo";

export type GlobalFlattenSafeState =
  | { status: "unknown" }
  | {
      status: "proven_flat";
      source: "broker" | "demo";
      mode: GlobalFlattenMode;
      proofScope: "global";
      subject: string;
      connectionEpoch: number;
      proofEpoch: string;
    };

export const UNKNOWN_GLOBAL_FLATTEN_SAFE_STATE = {
  status: "unknown",
} as const satisfies GlobalFlattenSafeState;

type GlobalFlattenSafeStateCache = Pick<
  QueryClient,
  "getQueryData" | "setQueryData"
>;

export const globalFlattenSafeStateQueryKey = (
  subject: string,
  connectionEpoch: number,
  mode: GlobalFlattenMode = "live",
) =>
  [
    ...GLOBAL_FLATTEN_SAFE_STATE_QUERY_KEY,
    subject,
    connectionEpoch,
    mode,
  ] as const satisfies QueryKey;

const globalFlattenAttemptQueryKey = (
  subject: string,
  connectionEpoch: number,
  mode: GlobalFlattenMode,
) =>
  [
    ...GLOBAL_FLATTEN_ATTEMPT_QUERY_KEY,
    subject,
    connectionEpoch,
    mode,
  ] as const satisfies QueryKey;

type GlobalFlattenAttempt = {
  executionId: string;
  subject: string;
  connectionEpoch: number;
  mode: GlobalFlattenMode;
};

export const getGlobalFlattenConnectionEpoch = (
  queryClient: Pick<QueryClient, "getQueryData">,
): number =>
  queryClient.getQueryData<number>(GLOBAL_FLATTEN_CONNECTION_EPOCH_QUERY_KEY) ??
  0;

export const bumpGlobalFlattenConnectionEpoch = (
  queryClient: GlobalFlattenSafeStateCache,
): number => {
  let nextEpoch = 0;
  queryClient.setQueryData<number>(
    GLOBAL_FLATTEN_CONNECTION_EPOCH_QUERY_KEY,
    (currentEpoch) => {
      nextEpoch = (currentEpoch ?? 0) + 1;
      return nextEpoch;
    },
  );
  return nextEpoch;
};

export function useGlobalFlattenConnectionEpoch(): number {
  return useQuery({
    queryKey: GLOBAL_FLATTEN_CONNECTION_EPOCH_QUERY_KEY,
    queryFn: async () => 0,
    initialData: 0,
    staleTime: Infinity,
  }).data;
}

export function useGlobalFlattenSafeState(
  subject: string | null,
  connectionEpoch: number,
  mode: GlobalFlattenMode,
) {
  return useQuery({
    queryKey: globalFlattenSafeStateQueryKey(
      subject ?? "unauthenticated",
      connectionEpoch,
      mode,
    ),
    queryFn: async (): Promise<GlobalFlattenSafeState> =>
      UNKNOWN_GLOBAL_FLATTEN_SAFE_STATE,
    enabled: subject !== null,
    initialData: UNKNOWN_GLOBAL_FLATTEN_SAFE_STATE,
    staleTime: Infinity,
  });
}

export function readGlobalFlattenSafeState(
  queryClient: Pick<QueryClient, "getQueryData">,
  subject: string,
  mode: GlobalFlattenMode = "live",
): GlobalFlattenSafeState {
  return (
    queryClient.getQueryData<GlobalFlattenSafeState>(
      globalFlattenSafeStateQueryKey(
        subject,
        getGlobalFlattenConnectionEpoch(queryClient),
        mode,
      ),
    ) ?? UNKNOWN_GLOBAL_FLATTEN_SAFE_STATE
  );
}

export function markGlobalFlattenUnknown(
  queryClient: GlobalFlattenSafeStateCache,
  subject?: string,
): void {
  if (subject) {
    queryClient.setQueryData(
      globalFlattenSafeStateQueryKey(
        subject,
        getGlobalFlattenConnectionEpoch(queryClient),
      ),
      UNKNOWN_GLOBAL_FLATTEN_SAFE_STATE,
    );
    return;
  }
  bumpGlobalFlattenConnectionEpoch(queryClient);
}

export function markGlobalFlattenProven(
  queryClient: GlobalFlattenSafeStateCache,
  proof: {
    source: "broker" | "demo";
    proofScope: GlobalFlattenProofScope;
    subject: string;
    proofEpoch: string;
    mode?: GlobalFlattenMode;
  },
): void {
  if (proof.proofScope !== "global" || !proof.subject || !proof.proofEpoch) {
    markGlobalFlattenUnknown(queryClient, proof.subject);
    return;
  }
  const connectionEpoch = getGlobalFlattenConnectionEpoch(queryClient);
  const mode = proof.mode ?? (proof.source === "demo" ? "demo" : "live");
  queryClient.setQueryData<GlobalFlattenSafeState>(
    globalFlattenSafeStateQueryKey(proof.subject, connectionEpoch, mode),
    (): GlobalFlattenSafeState => ({
      status: "proven_flat",
      source: proof.source,
      mode,
      proofScope: "global",
      subject: proof.subject,
      connectionEpoch,
      proofEpoch: proof.proofEpoch,
    }),
  );
}

// Executions whose authority has been CONSUMED (proof landed). Tier A: a
// consumed execution id can never mint a second attempt — replaying the same
// accepted HTTP response (or its frames) must not recreate authority.
const globalFlattenConsumedQueryKey = (
  subject: string,
  mode: GlobalFlattenMode,
) => ["global-flatten-consumed", subject, mode] as const satisfies QueryKey;

const isGlobalFlattenExecutionConsumed = (
  queryClient: Pick<QueryClient, "getQueryData">,
  subject: string,
  mode: GlobalFlattenMode,
  executionId: string,
): boolean =>
  Boolean(
    queryClient.getQueryData<Record<string, true>>(
      globalFlattenConsumedQueryKey(subject, mode),
    )?.[executionId],
  );

const markGlobalFlattenExecutionConsumed = (
  queryClient: GlobalFlattenSafeStateCache,
  subject: string,
  mode: GlobalFlattenMode,
  executionId: string,
): void => {
  queryClient.setQueryData<Record<string, true>>(
    globalFlattenConsumedQueryKey(subject, mode),
    (current) => ({ ...(current ?? {}), [executionId]: true }),
  );
};

export function beginGlobalFlattenAttempt(
  queryClient: GlobalFlattenSafeStateCache,
  attempt: {
    executionId: string | number;
    subject: string;
    mode: GlobalFlattenMode;
  },
): void {
  const executionId = String(attempt.executionId);
  if (!attempt.subject || !executionId || executionId === "0") return;
  // A consumed execution can never re-begin (flatten-hardening contract a):
  // replaying the accepted HTTP response after completion is a no-op.
  if (
    isGlobalFlattenExecutionConsumed(
      queryClient,
      attempt.subject,
      attempt.mode,
      executionId,
    )
  ) {
    return;
  }
  // Duplicate local begin is IDEMPOTENT: the same accepted execution observed
  // twice keeps ONE attempt and ONE epoch bump — a double-bump orphaned the
  // live attempt and forced an unnecessary re-proof (fail-safe but wrong).
  const currentEpoch = getGlobalFlattenConnectionEpoch(queryClient);
  const existing = queryClient.getQueryData<GlobalFlattenAttempt>(
    globalFlattenAttemptQueryKey(attempt.subject, currentEpoch, attempt.mode),
  );
  if (existing && existing.executionId === executionId) {
    return;
  }
  const connectionEpoch = bumpGlobalFlattenConnectionEpoch(queryClient);
  queryClient.setQueryData<GlobalFlattenAttempt>(
    globalFlattenAttemptQueryKey(
      attempt.subject,
      connectionEpoch,
      attempt.mode,
    ),
    {
      executionId,
      subject: attempt.subject,
      connectionEpoch,
      mode: attempt.mode,
    },
  );
}

/** True when a LIVE local attempt with this execution id exists at the
 * current epoch — the only thing a websocket emergency frame may act on.
 * Frames never begin attempts (contract c). */
export function hasLiveGlobalFlattenAttempt(
  queryClient: Pick<QueryClient, "getQueryData">,
  attempt: {
    executionId: string | number;
    subject: string;
    mode: GlobalFlattenMode;
  },
): boolean {
  const current = queryClient.getQueryData<GlobalFlattenAttempt>(
    globalFlattenAttemptQueryKey(
      attempt.subject,
      getGlobalFlattenConnectionEpoch(queryClient),
      attempt.mode,
    ),
  );
  return Boolean(current && current.executionId === String(attempt.executionId));
}

export function completeGlobalFlattenAttempt(
  queryClient: GlobalFlattenSafeStateCache,
  proof: {
    executionId: string | number;
    source: "broker" | "demo";
    mode: GlobalFlattenMode;
    proofScope: GlobalFlattenProofScope;
    subject: string;
    proofEpoch: string;
  },
): boolean {
  const connectionEpoch = getGlobalFlattenConnectionEpoch(queryClient);
  const attemptKey = globalFlattenAttemptQueryKey(
    proof.subject,
    connectionEpoch,
    proof.mode,
  );
  const attempt = queryClient.getQueryData<GlobalFlattenAttempt>(attemptKey);
  if (
    !attempt ||
    attempt.executionId !== String(proof.executionId) ||
    attempt.connectionEpoch !== connectionEpoch ||
    proof.proofScope !== "global"
  ) {
    return false;
  }
  queryClient.setQueryData(attemptKey, undefined);
  // Authority is CONSUMED: this execution id can never begin again.
  markGlobalFlattenExecutionConsumed(
    queryClient,
    proof.subject,
    proof.mode,
    String(proof.executionId),
  );
  markGlobalFlattenProven(queryClient, proof);
  return true;
}
